<!--
  MedicalInfoCard — read view of a player's structured medical information
  with an optional Edit button (opens MedicalConditionsEditor in a modal).

  Drop it anywhere a player's id is known (profile, settings). Visibility
  rules stay with the PARENT (profile.canViewMedical): the card assumes it is
  only rendered for the player themself, their parent, admins and coaches.

  Emits `saved` with the record returned by the API after an edit — parents
  that also hold `selectedUserData.medical` should refresh that from
  record.summary so an unrelated "save profile" can't overwrite it with a
  stale value.
-->
<template>
  <div class="mic" :class="{ 'mic--alert': record && record.hasConditions === 1 }">
    <div class="mic__head">
      <h4 class="mic__title">
        <i class="bi bi-heart-pulse"></i> Medical information
        <span v-if="record" class="mic__badge" :class="badgeClass">{{ badgeText }}</span>
      </h4>
      <button v-if="canEdit && record" type="button" class="mic__edit" @click="openEdit">
        <i class="bi bi-pencil"></i> {{ record.hasConditions === null ? "Add" : "Edit" }}
      </button>
    </div>

    <p v-if="loading" class="mic__muted">Loading...</p>
    <p v-else-if="error" class="mic__error">{{ error }}</p>

    <template v-else-if="record">
      <p v-if="record.hasConditions === null && !record.notes" class="mic__muted">
        Not yet declared. <span v-if="canEdit">Use Add to record whether there is anything staff should know.</span>
      </p>
      <p v-else-if="record.hasConditions === 0" class="mic__muted mic__muted--ok">
        <i class="bi bi-check-circle"></i> No medical conditions declared.
      </p>

      <ul v-if="record.conditions.length" class="mic__list">
        <li v-for="c in record.conditions" :key="c.id" class="mic__cond">
          <div class="mic__cond-head">
            <strong>{{ c.condition_name }}</strong>
            <span class="mic__chip">{{ c.category_label }}</span>
            <span v-if="c.severity" class="mic__chip" :class="'mic__chip--' + c.severity">
              {{ severityLabel(c.severity) }}
            </span>
          </div>
          <dl class="mic__facts">
            <template v-if="c.medication">
              <dt><i class="bi bi-capsule"></i> Medication</dt>
              <dd>
                {{ c.medication }}<span v-if="c.dosage_instructions"> - {{ c.dosage_instructions }}</span>
                <span v-if="c.carries_medication === 1" class="mic__carry mic__carry--yes">carries it</span>
                <span v-else-if="c.carries_medication === 0" class="mic__carry mic__carry--no">does NOT carry it</span>
              </dd>
            </template>
            <template v-if="c.triggers">
              <dt><i class="bi bi-exclamation-triangle"></i> Triggers</dt>
              <dd>{{ c.triggers }}</dd>
            </template>
            <template v-if="c.emergency_action">
              <dt><i class="bi bi-bandaid"></i> If it happens</dt>
              <dd class="mic__action">{{ c.emergency_action }}</dd>
            </template>
            <template v-if="c.notes">
              <dt>Notes</dt>
              <dd>{{ c.notes }}</dd>
            </template>
          </dl>
        </li>
      </ul>

      <p v-if="record.notes" class="mic__notes">{{ record.notes }}</p>

      <p v-if="record.updatedAt" class="mic__meta">Last updated {{ formatDate(record.updatedAt) }}</p>
    </template>

    <!-- Edit modal -->
    <div v-if="showEdit" class="mic-modal" @click.self="closeEdit">
      <div class="mic-modal__dialog" role="dialog" aria-modal="true" aria-label="Edit medical information">
        <div class="mic-modal__head">
          <h3>Medical information</h3>
          <button type="button" class="mic-modal__close" @click="closeEdit" aria-label="Close">&times;</button>
        </div>
        <div class="mic-modal__body">
          <MedicalConditionsEditor v-model="draft" :show-privacy-hint="false" />
          <p v-if="saveError" class="mic__error">{{ saveError }}</p>
        </div>
        <div class="mic-modal__foot">
          <button type="button" class="mic-modal__btn" @click="closeEdit" :disabled="saving">Cancel</button>
          <button type="button" class="mic-modal__btn mic-modal__btn--primary" @click="save"
            :disabled="saving || !draftValid">
            {{ saving ? "Saving..." : "Save" }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { Medical } from "@/services/medical-service.js";
import MedicalConditionsEditor from "./MedicalConditionsEditor.vue";

export default {
  name: "MedicalInfoCard",
  components: { MedicalConditionsEditor },
  props: {
    playerId: { type: [Number, String], required: true },
    clubId: { type: [Number, String], default: null },
    canEdit: { type: Boolean, default: false },
    /** users.id of the person viewing — stamped as updated_by on save. */
    viewerId: { type: [Number, String], default: null },
  },
  emits: ["saved"],
  data() {
    return {
      loading: false,
      error: null,
      record: null,
      showEdit: false,
      draft: { hasConditions: null, conditions: [], notes: "" },
      saving: false,
      saveError: null,
    };
  },
  computed: {
    badgeText() {
      if (!this.record) return "";
      if (this.record.hasConditions === 1) {
        const n = this.record.conditions.length;
        return n ? `${n} condition${n === 1 ? "" : "s"}` : "See notes";
      }
      if (this.record.hasConditions === 0) return "None declared";
      return this.record.notes ? "See notes" : "Not declared";
    },
    badgeClass() {
      if (!this.record) return "";
      if (this.record.hasConditions === 1) return "mic__badge--alert";
      if (this.record.hasConditions === 0) return "mic__badge--ok";
      return "mic__badge--muted";
    },
    draftValid() {
      if (this.draft.hasConditions !== 1) return this.draft.hasConditions === 0;
      // Every condition needs at least a name or a medication.
      return this.draft.conditions.every(
        (c) => (c.condition_name && c.condition_name.trim()) || (c.medication && c.medication.trim()),
      );
    },
  },
  watch: {
    playerId: {
      immediate: true,
      handler() {
        this.load();
      },
    },
  },
  methods: {
    async load() {
      if (!this.playerId) return;
      this.loading = true;
      this.error = null;
      try {
        const res = await Medical.get(this.playerId);
        if (res.data && res.data.status === "ok") {
          this.record = res.data.data;
        } else {
          this.error = (res.data && res.data.message) || "Could not load medical information.";
        }
      } catch (e) {
        console.error(e);
        this.error = "Could not load medical information.";
      } finally {
        this.loading = false;
      }
    },
    openEdit() {
      const r = this.record || {};
      this.draft = {
        hasConditions: r.hasConditions,
        conditions: (r.conditions || []).map((c) => ({ ...Medical.blankCondition(), ...c })),
        notes: r.notes || "",
      };
      // Legacy free text with no declaration: default the toggle to Yes so
      // the notes stay visible in the editor.
      if (this.draft.hasConditions === null && this.draft.notes) {
        this.draft.hasConditions = 1;
        if (!this.draft.conditions.length) this.draft.conditions = [Medical.blankCondition()];
      }
      this.saveError = null;
      this.showEdit = true;
    },
    closeEdit() {
      if (this.saving) return;
      this.showEdit = false;
    },
    async save() {
      this.saving = true;
      this.saveError = null;
      try {
        const res = await Medical.save({
          playerId: this.playerId,
          clubId: this.clubId,
          hasConditions: this.draft.hasConditions,
          conditions: this.draft.conditions,
          notes: this.draft.notes,
          updatedBy: this.viewerId,
        });
        if (res.data && res.data.status === "ok") {
          this.record = res.data.data;
          this.showEdit = false;
          this.$emit("saved", this.record);
        } else {
          this.saveError = (res.data && res.data.message) || "Could not save.";
        }
      } catch (e) {
        console.error(e);
        this.saveError = "Could not save medical information.";
      } finally {
        this.saving = false;
      }
    },
    severityLabel(v) {
      const s = Medical.SEVERITIES.find((x) => x.value === v);
      return s ? s.label : v;
    },
    formatDate(d) {
      try {
        return new Date(String(d).replace(" ", "T")).toLocaleDateString("en-GB", {
          day: "numeric",
          month: "short",
          year: "numeric",
        });
      } catch (e) {
        return d;
      }
    },
  },
};
</script>

<style scoped lang="scss">
.mic {
  margin-top: 0.75rem;
  padding: 10px 12px;
  background: #f8fafc;
  border: 1px solid #e5e7eb;
  border-left: 3px solid #9ca3af;
  border-radius: 8px;
  text-align: left;

  &--alert {
    border-left-color: #ef4444;
    background: #fff7f7;
  }

  &__head {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 0.5rem;
  }

  &__title {
    margin: 0;
    font-size: 0.9rem;
    color: #374151;
    display: flex;
    align-items: center;
    gap: 6px;
    flex-wrap: wrap;
  }

  &__badge {
    font-size: 0.7rem;
    font-weight: 600;
    padding: 2px 8px;
    border-radius: 999px;
    background: #e5e7eb;
    color: #374151;

    &--alert {
      background: #fee2e2;
      color: #b91c1c;
    }

    &--ok {
      background: #d1fae5;
      color: #047857;
    }

    &--muted {
      background: #e5e7eb;
      color: #6b7280;
    }
  }

  &__edit {
    border: 1px solid #d1d5db;
    background: #fff;
    border-radius: 6px;
    padding: 3px 10px;
    font-size: 0.8rem;
    cursor: pointer;
    color: #374151;
    white-space: nowrap;

    &:hover {
      border-color: #374151;
    }
  }

  &__muted {
    margin: 6px 0 0;
    font-size: 0.85rem;
    color: #6b7280;

    &--ok {
      color: #047857;
    }
  }

  &__error {
    margin: 6px 0 0;
    font-size: 0.85rem;
    color: #b91c1c;
  }

  &__list {
    list-style: none;
    padding: 0;
    margin: 8px 0 0;
  }

  &__cond {
    padding: 8px 0;
    border-top: 1px solid #f1f5f9;

    &:first-child {
      border-top: none;
    }
  }

  &__cond-head {
    display: flex;
    align-items: center;
    gap: 6px;
    flex-wrap: wrap;
    font-size: 0.9rem;
    color: #1f2937;
  }

  &__chip {
    font-size: 0.7rem;
    padding: 1px 7px;
    border-radius: 999px;
    background: #e5e7eb;
    color: #374151;

    &--mild {
      background: #ecfdf5;
      color: #047857;
    }

    &--moderate {
      background: #fef3c7;
      color: #b45309;
    }

    &--severe,
    &--life_threatening {
      background: #fee2e2;
      color: #b91c1c;
      font-weight: 700;
    }
  }

  &__facts {
    margin: 4px 0 0;
    font-size: 0.82rem;
    display: grid;
    grid-template-columns: max-content 1fr;
    column-gap: 10px;
    row-gap: 2px;

    dt {
      color: #6b7280;
      font-weight: 500;
      white-space: nowrap;
    }

    dd {
      margin: 0;
      color: #374151;
    }
  }

  &__action {
    font-weight: 600;
    color: #1f2937;
  }

  &__carry {
    margin-left: 6px;
    font-size: 0.72rem;
    padding: 0 6px;
    border-radius: 999px;

    &--yes {
      background: #d1fae5;
      color: #047857;
    }

    &--no {
      background: #fee2e2;
      color: #b91c1c;
    }
  }

  &__notes {
    margin: 8px 0 0;
    font-size: 0.85rem;
    color: #4b5563;
    white-space: pre-line;
  }

  &__meta {
    margin: 6px 0 0;
    font-size: 0.72rem;
    color: #9ca3af;
  }
}

.mic-modal {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 1060;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;

  &__dialog {
    background: #fff;
    border-radius: 10px;
    width: 100%;
    max-width: 760px;
    max-height: 92vh;
    display: flex;
    flex-direction: column;
    box-shadow: 0 10px 40px rgba(0, 0, 0, 0.25);
  }

  &__head {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 12px 16px;
    border-bottom: 1px solid #e5e7eb;

    h3 {
      margin: 0;
      font-size: 1.05rem;
    }
  }

  &__close {
    border: none;
    background: none;
    font-size: 1.5rem;
    line-height: 1;
    cursor: pointer;
    color: #6b7280;
  }

  &__body {
    padding: 16px;
    overflow-y: auto;
  }

  &__foot {
    display: flex;
    justify-content: flex-end;
    gap: 0.5rem;
    padding: 12px 16px;
    border-top: 1px solid #e5e7eb;
  }

  &__btn {
    border: 1px solid #d1d5db;
    background: #fff;
    border-radius: 6px;
    padding: 6px 16px;
    cursor: pointer;

    &--primary {
      background: #1f2937;
      border-color: #1f2937;
      color: #fff;
    }

    &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }
  }
}
</style>
